import Router from "next/router";
import ReactDOM from "react-dom";

import { resolveHref } from "next/dist/client/resolve-href";
import type { ParsedUrlQuery } from "querystring";

import { getRouteRegex } from "next/dist/shared/lib/router/utils/route-regex";
import { getRouteMatcher } from "next/dist/shared/lib/router/utils/route-matcher";

// eslint-disable-next-line import/no-extraneous-dependencies
import { BYSTADIUM_HOSTNAME } from "@stadium/helpers/app-routes";

export const disableBodyScroll = (stopScrollClass = "overflow-hidden") =>
  typeof window !== "undefined" && window.document.body.classList.add(stopScrollClass);

export const enableBodyScroll = (stopScrollClass = "overflow-hidden") =>
  typeof window !== "undefined" && window.document.body.classList.remove(stopScrollClass);

export const addClassToBody = (classToAdd = "") =>
  typeof window !== "undefined" && window.document.body.classList.add(classToAdd);

export const removeClassFromBody = (classToRemove = "") =>
  typeof window !== "undefined" && window.document.body.classList.remove(classToRemove);

export const downloadBlobAsPDF = ({ data, fileName }) => {
  if (typeof window === "undefined") {
    throw new ReferenceError("window is not defined");
  }
  if (data) {
    const blob = new Blob([data], { type: "application/pdf" });
    const fileURL = URL.createObjectURL(blob);
    const link = document.createElement("a");
    // @ts-ignore
    if (navigator.msSaveBlob) {
      // @ts-ignore
      navigator.msSaveBlob(blob, fileName);
    } else {
      link.download = fileName;
      link.href = fileURL;
      document.body.appendChild(link);
      link.click();
      window.setTimeout(() => {
        URL.revokeObjectURL(fileURL);
        document.body.removeChild(link);
      }, 100);
    }
  }
};

export const downloadBlob = ({ data, fileName }) => {
  if (typeof window === "undefined") {
    throw new ReferenceError("window is not defined");
  }
  if (data) {
    const blob = new Blob([data]);
    const fileURL = URL.createObjectURL(blob);
    const link = document.createElement("a");

    // @ts-ignore
    if (navigator.msSaveBlob) {
      // @ts-ignore
      navigator.msSaveBlob(blob, fileName);
    } else {
      link.download = fileName;
      link.href = fileURL;
      document.body.appendChild(link);
      link.click();
      window.setTimeout(() => {
        URL.revokeObjectURL(fileURL);
        document.body.removeChild(link);
      }, 100);
    }
  }
};

type DownloadBlobAsCSVOptions = { fileName: string; encodeDownloadFileInUTF8?: boolean } & (
  | { data: Blob; blob: Blob }
  | { data?: Blob; blob: Blob }
);

export const downloadBlobAsCSV = ({
  data,
  fileName,
  blob,
  encodeDownloadFileInUTF8,
}: DownloadBlobAsCSVOptions) => {
  if (typeof window === "undefined") {
    throw new ReferenceError("window is not defined");
  }
  if (data || blob) {
    const BOM = encodeDownloadFileInUTF8 ? ["\uFEFF"] : []; //BOM stands for byte-order-mark
    const csvBlob = new Blob([...BOM, data || blob], {
      type: `text/csv;${encodeDownloadFileInUTF8 ? "charset=utf-8;" : ""}`,
    });
    const fileURL = URL.createObjectURL(csvBlob);
    const link = document.createElement("a");
    // @ts-ignore
    if (navigator.msSaveBlob) {
      // @ts-ignore
      navigator.msSaveBlob(csvBlob, fileName);
    } else {
      link.download = fileName;
      link.href = fileURL;
      document.body.appendChild(link);
      link.click();
      window.setTimeout(() => {
        URL.revokeObjectURL(fileURL);
        document.body.removeChild(link);
      }, 100);
    }
  }
};

export const fetchFileFromUrl = async (url, name = "") => {
  let blob = await fetch(url).then((r) => r.blob());
  if (blob) {
    return {
      blob,
      size: blob.size,
      type: blob.type,
      name: name || url?.substring(url?.lastIndexOf("/") + 1),
    };
  }
};

export const getFileFromBlob = (blobObj) =>
  new File([blobObj.blob], blobObj.name, { type: blobObj.type });

export const openPopup = (url, winHeight = 400, winWidth = 500) => {
  const winTop = (screen.height - winWidth) / 2;
  const winLeft = (screen.width - winHeight) / 2;

  return window.open(
    url,
    "_blank",
    `top=${winTop},left=${winLeft},toolbar=0,status=0,width=${winWidth},height=${winHeight},popup`,
  );
};

export const addClassToElement = (elem, classToAdd = "") =>
  classToAdd ? elem?.classList.add(classToAdd) : null;

export const removeClassFromElement = (elem, classToRemove = "") =>
  classToRemove ? elem?.classList.remove(classToRemove) : null;

export const openExternalLink = (url, option = `rel='noreferrer noopener'`) =>
  window.open(url, "_blank", option);

export const stripHTML = (htmlContent = "") => {
  const newElement = document.createElement("p");
  newElement.innerHTML = htmlContent ?? "";
  return newElement.textContent?.trim().replace(/(\r\n|\n|\r)/gm, "");
};

export const truncateHTML = (htmlContent: string, maxLength: number): string => {
  if (!htmlContent || typeof document === "undefined") return htmlContent;

  const plainText = stripHTML(htmlContent);
  if (!plainText || plainText.length <= maxLength) {
    return htmlContent;
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;

  let charCount = 0;
  const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, null);

  let node: Node | null;
  let nodesToRemove: Node[] = [];

  while ((node = walker.nextNode())) {
    const text = node.textContent || "";

    if (charCount + text.length <= maxLength) {
      charCount += text.length;
    } else {
      // Truncate the current node to fit within maxLength
      const remainingChars = maxLength - charCount;
      if (remainingChars > 0 && node.textContent) {
        node.textContent = node.textContent.substring(0, remainingChars) + "...";
      } else {
        // If no remaining chars, mark this node for removal
        nodesToRemove.push(node);
      }

      // Collect all subsequent nodes for removal
      let nextNode = walker.nextNode();
      while (nextNode) {
        nodesToRemove.push(nextNode);
        nextNode = walker.nextNode();
      }
      break;
    }
  }

  // Remove collected nodes
  nodesToRemove.forEach((nodeToRemove) => {
    if (nodeToRemove.parentNode) {
      nodeToRemove.parentNode.removeChild(nodeToRemove);
    }
  });

  return tempDiv.innerHTML;
};

type GenerateUrlByQueryOptions = {
  query: ParsedUrlQuery;
  pathname: string;
  baseOrigin?: string;
};

/**
 * Accepts query object from nextjs router and converts internal pathname like `/[countryCode]/[lang]/stores
 * to /us/en/stores` when query object is `{ countryCode: us, lang: en }`. Helpful for directly getting `asPath`
 * from router pathname or converting it to a subpath.
 * Uses `interpolateAs` from next/router to make it fully compatible.
 * Strips `meta` (path-only dynamic segment in app/[meta] routes) so it is not serialized as `?meta=`.
 */
export const generateUrlByQuery = ({
  query,
  pathname,
  baseOrigin: _baseOrigin,
}: GenerateUrlByQueryOptions) => {
  const sanitizedQuery = { ...query };
  delete (sanitizedQuery as Record<string, unknown>).meta;

  let baseUrl = new URL(pathname, _baseOrigin || BYSTADIUM_HOSTNAME);

  try {
    let urlOrigin =
      _baseOrigin ||
      (typeof window !== "undefined" && window.location.origin) ||
      BYSTADIUM_HOSTNAME;

    const baseOrigin = new URL(pathname, urlOrigin).origin;

    const [, newBase] = resolveHref(
      Router,
      { query: sanitizedQuery, pathname: new URL(pathname, baseOrigin).pathname },
      true,
    );
    baseUrl = new URL(newBase, baseOrigin);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return baseUrl.toString();
};

export const removeTrailingSlash = (val: string) =>
  val.endsWith("/") ? val.substring(0, val.length - 1) : val;

//--TODO: Handle case for "/path/[[...code]]"--//
// /**
//  * Accepts a url to test against a dynamic pathname
//  * `true` if it fully matches
//  */
export const matchUrl = ({ url: asPath, pathname }: { url: string; pathname: string }) => {
  if (asPath === pathname) {
    return true;
  }

  const baseAsPath = removeTrailingSlash(asPath.split("?")[0]);
  const basePathname = removeTrailingSlash(pathname.split("?")[0]);
  if (baseAsPath === basePathname) {
    return true;
  }

  // Create regex for exact match
  const basePathRegex = new RegExp(
    `^${basePathname.replace(/(\[[a-zA-Z0-9-]+\])+/g, "[a-zA-Z0-9-]+")}$`
      .replace(/\[\[\.\.\.[a-zA-Z0-9-]+\]\]/g, "?.*")
      .replace(/\[\.\.\.[a-zA-Z0-9-]+\]/g, ".*"),
  );

  if (basePathRegex.test(baseAsPath)) {
    return true;
  }
  return false;
};

/**
 *
 * @param scriptTag
 * @returns Promise<isSrcPresent = boolean>
 */
export const loadScript = (scriptTag: string): Promise<boolean> =>
  new Promise((resolve, reject) => {
    const range = document.createRange();
    const contextualFrag = range.createContextualFragment(scriptTag);
    const script = contextualFrag.querySelector("script");
    script.onload = () => resolve(true);
    script.onerror = () => reject(Error(`failed to load ${scriptTag}`));
    document.head.appendChild(script);
    if (!script.src) {
      resolve(false);
    }
  });

export const generateImageUrlFromFile = (file) => {
  if (!file) {
    return;
  }

  return URL.createObjectURL(file.blob || file);
};

export const addNoBreakOnNodeRemoveChild = () => {
  // do not break if wordpress JS does some DOM manipulations
  if (typeof Node === "function" && Node.prototype) {
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function (child) {
      // not removing child if its parent node is not present
      if (child.parentNode !== this) {
        if (console) {
          // eslint-disable-next-line no-console
          console.log("Cannot remove a child from a different parent");
        }
        return child;
      }
      return originalRemoveChild.apply(this, arguments);
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function (newNode, referenceNode) {
      // not inserting newNode if reference node parent is not present
      if (referenceNode && referenceNode.parentNode !== this) {
        if (console) {
          // eslint-disable-next-line no-console
          console.log(
            "Cannot insert before a reference node from a different parent",
            referenceNode,
          );
        }
        return newNode;
      }
      return originalInsertBefore.apply(this, arguments);
    };
  }
};

export const scrollToElementById = (id: string) => {
  const element = document.getElementById(id);

  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: "smooth" });
};

export const isFontAvailable = (fontFamily: string) => {
  for (let font of Array.from(document.fonts)) {
    if (font.family === fontFamily) {
      return true;
    }
  }
  return false;
};

export const matchesUrl = ({
  url,
  pathname,
  bailOnMatch = true,
}: {
  url: string;
  pathname: string | string[];
  bailOnMatch?: boolean;
}) => {
  if (Array.isArray(pathname)) {
    return pathname[bailOnMatch ? "some" : "every"]((p) => matchUrl({ url, pathname: p }));
  }
  return matchUrl({ url, pathname });
};

/**
 * Extracts route parameters from a pathname based on a dynamic route pattern
 * using Next.js internal utilities
 *
 * The dynamic route pattern (e.g., "/users/[id]/posts/[postId]")
 * The current pathname (e.g., "/users/123/posts/abc")
 * Returns an object containing the extracted parameters or false if no match
 */
export function getRouteParams(
  dynamicRoute: string,
  pathname: string,
): Record<string, any> | false {
  if (!dynamicRoute || !pathname) {
    return {};
  }

  try {
    // Generate the route regex using Next.js internal utility
    const routeRegex = getRouteRegex(dynamicRoute);

    // Create a route matcher function
    const routeMatcher = getRouteMatcher(routeRegex);

    // Extract parameters using the matcher
    return routeMatcher(pathname);
  } catch (error) {
    return {};
  }
}


export const patchReactDOM = () => {
  if (typeof ReactDOM.findDOMNode === "undefined") {
    // Define a local polyfill that more closely matches React 17 semantics.
    // - If given a DOM Element or Text node, just return it (original behavior).
    // - If given a React component instance, walk its fiber tree to find the
    //   first host DOM node within that component's subtree (not the root container).
    const findDOMNodePolyfill = function (componentOrElement: any): Element | Text | null {
      if (componentOrElement == null) return null;

      // If it's already a DOM node (Element or Text), return as‑is.
      const nodeType = (componentOrElement as any).nodeType;
      if (nodeType === 1 || nodeType === 3) {
        return componentOrElement as Element | Text;
      }

      // React 18 stores the fiber on _reactInternals (or legacy _reactInternalFiber)
      let fiber =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (componentOrElement as any)._reactInternals ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (componentOrElement as any)._reactInternalFiber;

      if (!fiber) {
        // Not a React component (or unmounted) – match React behavior by returning null
        return null;
      }

      // Depth‑first search for the first host node (DOM element or text node)
      const findFirstHostNode = (fiberNode: any): Element | Text | null => {
        if (!fiberNode) return null;

        const stateNode = fiberNode.stateNode;
        if (stateNode && (stateNode.nodeType === 1 || stateNode.nodeType === 3)) {
          return stateNode as Element | Text;
        }

        let child = fiberNode.child;
        while (child) {
          const result = findFirstHostNode(child);
          if (result) return result;
          child = child.sibling;
        }

        return null;
      };

      return findFirstHostNode(fiber);
    };

    // Attach the polyfill to ReactDOM
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ReactDOM as any).findDOMNode = findDOMNodePolyfill;
  }

  // @ts-ignore
  if (ReactDOM.default && typeof ReactDOM.default.findDOMNode === "undefined") {
    // Keep default export in sync
    // @ts-ignore
    ReactDOM.default.findDOMNode = ReactDOM.findDOMNode;
  }

  // Also patch module cache if available
  if (typeof require !== "undefined" && require.cache) {
    try {
      const reactDomPath = require.resolve("react-dom");
      if (require.cache[reactDomPath]) {
        const cachedModule = require.cache[reactDomPath];
        if (cachedModule.exports && typeof cachedModule.exports.findDOMNode === "undefined") {
          cachedModule.exports.findDOMNode = ReactDOM.findDOMNode;
        }
        if (cachedModule.exports.default && typeof cachedModule.exports.default.findDOMNode === "undefined") {
          cachedModule.exports.default.findDOMNode = ReactDOM.findDOMNode;
        }
      }
    } catch (e) {
      // Ignore errors
    }
  }
}